/**
 * Custom glyphs from the Figma delivery-methods screens (node assets, traced 1:1).
 * Lucide has no equivalents: the courier truck carries InPost-yellow wheels, the
 * locker is a pixel-grid cabinet with one yellow door, and the add-market glyph
 * is a map-pin + magnifier with a yellow lens.
 */

type IconProps = { className?: string };

/** InPost Courier / Weekend Delivery - truck with yellow wheels (Figma 24×24). */
export function CourierTruckIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M14 18V6C14 5.46957 13.7893 4.96086 13.4142 4.58579C13.0391 4.21071 12.5304 4 12 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V17C2 17.2652 2.10536 17.5196 2.29289 17.7071C2.48043 17.8946 2.73478 18 3 18H5"
        fill="#fff"
        stroke="#171717"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M15 18H9" stroke="#171717" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M19 18H21C21.2652 18 21.5196 17.8946 21.7071 17.7071C21.8946 17.5196 22 17.2652 22 17V13.35C21.9996 13.1231 21.922 12.903 21.78 12.726L18.3 8.376C18.2065 8.25888 18.0878 8.16428 17.9528 8.0992C17.8178 8.03412 17.6699 8.00021 17.52 8H14"
        stroke="#171717"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z"
        fill="#FFCC00"
        stroke="#171717"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 20C8.10457 20 9 19.1046 9 18C9 16.8954 8.10457 16 7 16C5.89543 16 5 16.8954 5 18C5 19.1046 5.89543 20 7 20Z"
        fill="#FFCC00"
        stroke="#171717"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** InPost Parcel Lockers® 24/7 - pixel-grid cabinet, one yellow door (Figma 23.6×18). */
export function LockerIcon({ className }: IconProps) {
  const cells: Array<{ x: number; y: number; w: number; h: number; yellow?: boolean }> = [
    // Top row of large doors.
    { x: 0, y: 0, w: 8, h: 7.2 },
    { x: 8, y: 0, w: 8, h: 7.2 },
    { x: 15.6, y: 0, w: 8, h: 7.2 },
    // Right column of slim lockers.
    { x: 15.6, y: 7.2, w: 8, h: 2 },
    { x: 15.6, y: 8.8, w: 8, h: 2 },
    { x: 15.6, y: 10.8, w: 8, h: 2 },
    { x: 15.6, y: 12.4, w: 8, h: 2 },
    { x: 15.6, y: 14.4, w: 8, h: 2 },
    { x: 15.6, y: 16, w: 8, h: 2 },
    // Left + middle columns of medium doors (one highlighted).
    { x: 0, y: 7.2, w: 8, h: 3.6, yellow: true },
    { x: 8, y: 7.2, w: 8, h: 3.6 },
    { x: 0, y: 10.8, w: 8, h: 3.6 },
    { x: 8, y: 10.8, w: 8, h: 3.6 },
    { x: 0, y: 14.4, w: 8, h: 3.6 },
    { x: 8, y: 14.4, w: 8, h: 3.6 },
  ];

  return (
    <svg viewBox="-0.5 -0.5 24.6 19" fill="none" className={className} aria-hidden="true">
      {cells.map((cell) => (
        <rect
          key={`${cell.x}-${cell.y}`}
          x={cell.x}
          y={cell.y}
          width={cell.w}
          height={cell.h}
          fill={cell.yellow ? '#FFCC05' : 'none'}
          stroke="#171717"
          strokeWidth="0.6"
        />
      ))}
    </svg>
  );
}

/** Add a Market - map-pin + magnifier with yellow lens (Figma 32×32). */
export function AddMarketIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
      <path
        d="M16.3307 29.292C16.1371 29.3415 15.9348 29.3468 15.7389 29.3075C15.5429 29.2683 15.3583 29.1855 15.1987 29.0653C12.7187 26.924 5.33333 19.9907 5.33333 13.3333C5.33333 10.5044 6.45714 7.79125 8.45753 5.79086C10.4579 3.79047 13.171 2.66667 16 2.66667C18.829 2.66667 21.5421 3.79047 23.5425 5.79086C25.5429 7.79125 26.6667 10.5044 26.6667 13.3333C26.6667 13.8933 26.6147 14.4547 26.5173 15.016"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M29.3333 29.3333L26.8267 26.8267"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 17.3333C18.2091 17.3333 20 15.5425 20 13.3333C20 11.1242 18.2091 9.33333 16 9.33333C13.7909 9.33333 12 11.1242 12 13.3333C12 15.5425 13.7909 17.3333 16 17.3333Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M24 28C26.2091 28 28 26.2091 28 24C28 21.7909 26.2091 20 24 20C21.7909 20 20 21.7909 20 24C20 26.2091 21.7909 28 24 28Z"
        fill="#FFCC05"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Drag-handle dots (Figma 15×15, 2×3 grid). */
export function GripDotsIcon({ className }: IconProps) {
  const xs = [5.625, 9.375];
  const ys = [3.125, 7.5, 11.875];

  return (
    <svg viewBox="0 0 15 15" fill="none" className={className} aria-hidden="true">
      {xs.flatMap((x) =>
        ys.map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.9" fill="#888" stroke="#888" />),
      )}
    </svg>
  );
}
